import { useQueryClient } from '@tanstack/react-query';

import { usePatchPlot, usePatchPlotQuestionnaire, usePostPlot } from '@/api/rest/resources/plot';
import { PlotStatusEnum, PlotType, PostPlotRequestBodyData } from '@/api/rest/resources/types/plot';
import { useProjectDetailById } from '@/pages/shared/hooks/useProjectDetailById';
import { UnexpectedMissingDataError } from '@/utils/errors/UnexpectedMissingDataError';
import { transformMultiPolygonToPolygon } from '@/utils/transformPolygon';

import { buildQuestionnaireBody } from '../../../buildQuestionnaireBody';
import { PlotInputs } from '../../../types';
import { useDefaultPlotName } from './useDefaultPlotName';

export const useCreatePlot = () => {
  const queryClient = useQueryClient();

  const { data: project } = useProjectDetailById();

  const postPlotMutation = usePostPlot();
  const patchPlotQuestionnaireMutation = usePatchPlotQuestionnaire();
  const patchPlotMutation = usePatchPlot();

  const { setDefaultPlotNameCounter } = useDefaultPlotName();

  const submit = async (inputs: PlotInputs, options?: { draft?: boolean }) => {
    const { plotType, name, multiPolygon } = inputs;

    if (!plotType || !name || !multiPolygon || !project) {
      throw new UnexpectedMissingDataError({ dataLabels: { plotType, name, multiPolygon, project } });
    }

    if (plotType === PlotType.CROPLAND && !inputs.crops) {
      throw new UnexpectedMissingDataError({ dataLabels: { crops: inputs.crops } });
    }

    const questionnaireBody = buildQuestionnaireBody(inputs);

    let plotId = postPlotMutation.data?.id;
    const targetPlotStatus = options?.draft ? PlotStatusEnum.draft : PlotStatusEnum.ready_to_analyse;

    /**
     * In case postPlotMutation succeeds but patchPlotQuestionnaireMutation
     * fails, we make sure that any subsequent submit attempt only triggers
     * patchPlotQuestionnaireMutation with the existing plot ID.
     */
    if (!plotId) {
      const bodyData = {
        name,
        type: plotType,
        multi_polygon: multiPolygon,
        polygon: transformMultiPolygonToPolygon(multiPolygon), // TODO: MVP-9361 - remove transformMultiPolygonToPolygon when BE drops required polygon for plot POST request
        project: project?.id,
        organization: project.organization_id,
        status: targetPlotStatus,
      } as PostPlotRequestBodyData;

      if (plotType === PlotType.CROPLAND && inputs.crops) {
        bodyData.crops = inputs.crops;
      }

      const { id } = await postPlotMutation.mutateAsync({ bodyData });

      plotId = id;
    }

    await patchPlotQuestionnaireMutation.mutateAsync({
      pathVariables: { id: plotId },
      bodyData: {
        json: questionnaireBody,
      },
    });

    await patchPlotMutation.mutateAsync({
      pathVariables: { id: plotId },
      bodyData: {
        status: targetPlotStatus,
      },
    });

    queryClient.resetQueries({ queryKey: ['land_steward'] });
    setDefaultPlotNameCounter((c) => c + 1);
  };

  const isSubmitting = postPlotMutation.isPending || patchPlotQuestionnaireMutation.isPending;

  return { submit, isSubmitting };
};
